/**
 * Seminar request-body serializers.
 *
 * Phase 2: replaces the inline field-by-field "whitelist" that used to live in
 * hooks/seminars/useSeminarOperations.js (constructSeminarPayload). A serializer
 * is a pure function mapping the component's input state to the request body.
 *
 * The payload SHAPE is pinned two ways: the {@link SeminarPayload} interface
 * enumerates every key the API accepts, and the jest test asserts the complete
 * output object. Adding a column is now a one-place, checked change — extend the
 * interface + the serializer, and the test forces you to account for it.
 *
 * NOTE ON IDENTITY: relations are flattened to their NUMERIC `id` (raum.id,
 * ref.id) — this is the historical wire shape the Strapi backend expects for
 * these writes, so we deliberately do NOT route them through utils/identity.apiId
 * (which would emit documentId and change behavior).
 */
import type { Seminar, VbSemRef } from "../../types/entities";
import { addDays, toBareTime, toDateValue, toISODateString } from "../../utils/dateUtils";
import { relationId } from "../../utils/identity";

/**
 * A scalar seminar field as it arrives from (untyped) component state. Kept
 * loose on purpose: the form does not distinguish string/number/bool at rest.
 */
export type SeminarFieldValue = string | number | boolean | null | undefined;

/**
 * The seminar create/edit form state the serializer reads. Scalar fields are
 * loosely typed; the two relation fields that get flattened carry the exact
 * entity relation types from {@link Seminar}.
 */
export interface SeminarFormInput {
  // Strings / Texts
  thema?: SeminarFieldValue;
  lang_thema?: SeminarFieldValue;
  ort?: SeminarFieldValue;
  status?: SeminarFieldValue;
  marke?: SeminarFieldValue;
  verantwortlicher?: SeminarFieldValue;
  pocketpc?: SeminarFieldValue;
  ist_outlook?: SeminarFieldValue;
  soll_ref_hotel?: SeminarFieldValue;
  ist_ref_hotel?: SeminarFieldValue;
  soll_beamer?: SeminarFieldValue;
  ist_getraenke_bestell?: SeminarFieldValue;
  bemerkung?: SeminarFieldValue;
  wp_link?: SeminarFieldValue;
  audiences?: SeminarFieldValue;
  participants?: SeminarFieldValue;
  conditions_of_participation?: SeminarFieldValue;
  focus_areas?: SeminarFieldValue;
  discount_price?: SeminarFieldValue;
  preis?: SeminarFieldValue;
  preis_euro?: SeminarFieldValue;

  // Booleans
  wp_published?: SeminarFieldValue;
  is_confirmed?: SeminarFieldValue;
  is_course_full?: SeminarFieldValue;
  neuraum?: SeminarFieldValue;
  tagged_for_email?: SeminarFieldValue;

  // Numbers / Integers
  max_teilnehmer?: SeminarFieldValue;
  max_tn_zahl?: SeminarFieldValue;
  anzahl_skripten?: SeminarFieldValue;
  id_wp_entry?: SeminarFieldValue;
  id_teilnehmerliste?: SeminarFieldValue;

  // Dates / Times
  datum?: SeminarFieldValue;
  neu_datum?: SeminarFieldValue;
  end_datum?: SeminarFieldValue;
  // The two times may hold a Date while an edit is in flight (the TimePicker's
  // onChange value); the serializer folds it back to a bare "HH:mm:ss.SSS" string.
  anfangs_zeit?: SeminarFieldValue | Date;
  end_zeit?: SeminarFieldValue | Date;
  zahl_termin?: SeminarFieldValue;
  frage_termin?: SeminarFieldValue;
  discount_before_date?: SeminarFieldValue;

  // Relations (flattened to id by the serializer). Accept both the populated
  // object shape (from a selection) and the already-flat numeric id shape.
  raum?: Seminar["raum"] | null;
  vb_sem_refs?: Array<VbSemRef | number> | number | null;
}

/**
 * The seminar request body sent to seminarApi.create / seminarApi.update. Every
 * key the API accepts is enumerated here; a field missing from a serializer's
 * output is a regression caught by the unit test.
 */
export type SeminarPayload = {
  // Strings / Texts
  thema?: SeminarFieldValue;
  lang_thema?: SeminarFieldValue;
  ort?: SeminarFieldValue;
  status?: SeminarFieldValue;
  marke?: SeminarFieldValue;
  verantwortlicher?: SeminarFieldValue;
  pocketpc?: SeminarFieldValue;
  ist_outlook?: SeminarFieldValue;
  soll_ref_hotel?: SeminarFieldValue;
  ist_ref_hotel?: SeminarFieldValue;
  soll_beamer?: SeminarFieldValue;
  ist_getraenke_bestell?: SeminarFieldValue;
  bemerkung?: SeminarFieldValue;
  wp_link?: SeminarFieldValue;
  audiences?: SeminarFieldValue;
  participants?: SeminarFieldValue;
  conditions_of_participation?: SeminarFieldValue;
  focus_areas?: SeminarFieldValue;
  discount_price?: SeminarFieldValue;
  preis?: SeminarFieldValue;
  preis_euro?: SeminarFieldValue;

  // Booleans
  wp_published?: SeminarFieldValue;
  is_confirmed?: SeminarFieldValue;
  is_course_full?: SeminarFieldValue;
  neuraum?: SeminarFieldValue;
  tagged_for_email?: SeminarFieldValue;

  // Numbers / Integers
  max_teilnehmer?: SeminarFieldValue;
  max_tn_zahl?: SeminarFieldValue;
  anzahl_skripten?: SeminarFieldValue;
  id_wp_entry?: SeminarFieldValue;
  id_teilnehmerliste?: SeminarFieldValue;

  // Dates / Times — all bare strings ("yyyy-MM-dd" / "HH:mm:ss.SSS"). On create,
  // zahl_termin/frage_termin are computed bare dates (end_datum ± days).
  datum?: SeminarFieldValue;
  neu_datum?: SeminarFieldValue;
  end_datum?: SeminarFieldValue;
  anfangs_zeit?: SeminarFieldValue;
  end_zeit?: SeminarFieldValue;
  zahl_termin?: SeminarFieldValue;
  frage_termin?: SeminarFieldValue;
  discount_before_date?: SeminarFieldValue;

  // Relations (flattened to numeric id)
  raum?: number | null;
  vb_sem_refs?: number[] | number | null;
};

/**
 * An empty numeric input means "no value", not NaN.
 *
 * `<input type="number">` yields "" when cleared, and passing that to Strapi's
 * integer validator casts it to NaN and rejects the request — taking every other
 * edit in the same save with it. Anything else is coerced with Number() so a
 * numeric string ("25") arrives as a number.
 */
const toIntOrNull = (value: SeminarFieldValue): number | null =>
  value === "" || value === null || value === undefined ? null : Number(value);

/**
 * Base seminar payload: the exact allowed field set, with relations flattened to
 * their numeric id. Used directly for edit/update, and as the base for create.
 */
export function toSeminarPayload(rawInput: SeminarFormInput): SeminarPayload {
  return {
    // Strings / Texts
    thema: rawInput.thema,
    lang_thema: rawInput.lang_thema,
    ort: rawInput.ort,
    status: rawInput.status,
    marke: rawInput.marke,
    verantwortlicher: rawInput.verantwortlicher,
    pocketpc: rawInput.pocketpc,
    ist_outlook: rawInput.ist_outlook,
    soll_ref_hotel: rawInput.soll_ref_hotel,
    ist_ref_hotel: rawInput.ist_ref_hotel,
    soll_beamer: rawInput.soll_beamer,
    ist_getraenke_bestell: rawInput.ist_getraenke_bestell,
    bemerkung: rawInput.bemerkung,
    wp_link: rawInput.wp_link,
    audiences: rawInput.audiences,
    participants: rawInput.participants,
    conditions_of_participation: rawInput.conditions_of_participation,
    focus_areas: rawInput.focus_areas,
    discount_price: rawInput.discount_price,
    preis: rawInput.preis,
    preis_euro: rawInput.preis_euro,

    // Booleans
    wp_published: rawInput.wp_published,
    is_confirmed: rawInput.is_confirmed,
    is_course_full: rawInput.is_course_full,
    neuraum: rawInput.neuraum,
    tagged_for_email: rawInput.tagged_for_email,

    // Numbers / Integers
    //
    // The two user-editable ones go through toIntOrNull. Both are `type="number"`
    // inputs that hand `e.target.value` — a STRING — straight to the store, so
    // CLEARING the field sends "". Strapi casts that to NaN and rejects the whole
    // request ("max_tn_zahl must be a `number` type, but the final value was:
    // `NaN` (cast from the value \"\")"), so emptying Max. Teilnehmerzahl failed
    // the entire seminar save and the user's other edits went with it. Both
    // columns are nullable integers, so an empty field means null.
    max_teilnehmer: rawInput.max_teilnehmer,
    max_tn_zahl: toIntOrNull(rawInput.max_tn_zahl),
    anzahl_skripten: toIntOrNull(rawInput.anzahl_skripten),
    id_wp_entry: rawInput.id_wp_entry,
    id_teilnehmerliste: rawInput.id_teilnehmerliste,

    // Dates are bare "yyyy-MM-dd" already (from sanitizeSeminarValue / the store);
    // pass through. The two seminar times are bare "HH:mm:ss.SSS" wall clocks — a
    // picked Date serializes via toBareTime so the wall clock the user saw IS what's
    // stored (axios' default toISOString would shift it); untouched strings pass through.
    datum: rawInput.datum,
    neu_datum: rawInput.neu_datum,
    end_datum: rawInput.end_datum,
    anfangs_zeit: toBareTime(rawInput.anfangs_zeit),
    end_zeit: toBareTime(rawInput.end_zeit),
    zahl_termin: rawInput.zahl_termin,
    frage_termin: rawInput.frage_termin,
    discount_before_date: rawInput.discount_before_date,

    // Relations (flattened to id)
    raum: relationId(rawInput.raum),
    vb_sem_refs: Array.isArray(rawInput.vb_sem_refs)
      ? rawInput.vb_sem_refs.map((ref) => relationId(ref))
      : rawInput.vb_sem_refs,
  };
}

/**
 * Create/copy seminar payload. Layers the new-seminar overrides on top of the
 * base payload:
 *
 * - vb_sem_refs is REMOVED: a created/copied seminar must NOT reuse the source's
 *   referent junctions (connecting them re-points the manyToOne id_seminar off
 *   the original, stripping its referents) — referents for the new seminar are
 *   attached separately via referenteArray -> addSeminarReferentHandler.
 * - id_wp_entry / wp_link are nulled so a copy creates a NEW WordPress post
 *   instead of overwriting the original seminar's live post (updateWPHandler
 *   treats empty id_wp_entry as "create new").
 * - zahl_termin / frage_termin are computed from end_datum as bare "yyyy-MM-dd"
 *   (parse the bare date to a LOCAL Date so the ±day math is timezone-safe), and
 *   max_teilnehmer defaults to 0.
 */
export function toSeminarCreatePayload(rawInput: SeminarFormInput): SeminarPayload {
  const payload = toSeminarPayload(rawInput);
  delete payload.vb_sem_refs;
  const inputDate = toDateValue(rawInput.end_datum as string | Date | null | undefined);

  return {
    ...payload,
    id_wp_entry: null,
    wp_link: null,
    zahl_termin: inputDate ? (toISODateString(addDays(inputDate, 7)) as string) : null,
    frage_termin: inputDate ? (toISODateString(addDays(inputDate, -14)) as string) : null,
    max_teilnehmer: 0,
  };
}

/** The WordPress REST post result the seminar links back to. */
export interface WpPostResult {
  id?: number | string;
  generated_slug?: string;
}

/** The seminar↔WordPress linkage payload written back after the WP post exists. */
export type SeminarWpLinkagePayload = {
  id_wp_entry: WpPostResult["id"];
  wp_link: WpPostResult["generated_slug"];
};

/**
 * Second, tiny seminar write: link the freshly-created seminar back to its
 * newly-created WordPress post.
 */
export function toSeminarWpLinkagePayload(wpResult: WpPostResult): SeminarWpLinkagePayload {
  return {
    id_wp_entry: wpResult.id,
    wp_link: wpResult.generated_slug,
  };
}
